import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  Divider,
  Grid,
  CardMedia,
  LinearProgress,
} from '@mui/material';
import {
  CheckCircle as AcceptIcon,
  Cancel as DeclineIcon,
  Visibility as ViewIcon,
  Assignment as AssignmentIcon,
  AttachMoney as MoneyIcon,
  Cancel as CancelIcon,
  PhotoCamera as PhotoIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { acceptContractorQuote, declineContractorQuote, resendContractorInvite, resendInviteByInviteId, cancelContractorInvite, cancelInviteByInviteId } from '../services/quoteManagementService';
import { uploadJobPhoto, deleteJob } from '../services/contractorJobService';
import { getContractorJobPhotoUrl } from '../utils/photoUtils';

interface ContractorResponse {
  contractorId: string;
  contractorName: string;
  contractorEmail: string;
  amount: number;
  notes?: string;
  respondedAt: string;
  status: 'pending' | 'accepted' | 'declined';
}

interface JobWithResponsesProps {
  job: any;
  contractorTypeName?: string;
  onAcceptQuote: (jobId: string, contractorId: string) => Promise<void>;
  onDeclineQuote: (jobId: string, contractorId: string) => Promise<void>;
  onViewJobDetails: (job: any) => void;
  onCancelJob: (job: any) => void;
  onSendQuotes: (job: any) => void;
  onDeleteJob: (job: any) => void;
  onEditJob?: (job: any) => void;
  isAdmin: boolean;
}

export const JobWithResponses: React.FC<JobWithResponsesProps> = ({
  job,
  contractorTypeName,
  onAcceptQuote,
  onDeclineQuote,
  onViewJobDetails,
  onCancelJob,
  onSendQuotes,
  onDeleteJob,
  onEditJob,
  isAdmin,
}) => {
  const [acceptingQuote, setAcceptingQuote] = useState<string | null>(null);
  const [decliningQuote, setDecliningQuote] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<any>(null);
  const [invites, setInvites] = useState<any[]>(job.invites || []);

  useEffect(() => {
    setInvites(job.invites || []);
  }, [job.invites]);

  const handleAcceptQuote = async (contractorId: string) => {
    try {
      setAcceptingQuote(contractorId);
      await onAcceptQuote(job._id, contractorId);
    } catch (error) {
      console.error('Error accepting quote:', error);
    } finally {
      setAcceptingQuote(null);
    }
  };

  const handleDeclineQuote = async (contractorId: string) => {
    try {
      setDecliningQuote(contractorId);
      await onDeclineQuote(job._id, contractorId);
    } catch (error) {
      console.error('Error declining quote:', error);
    } finally {
      setDecliningQuote(null);
    }
  };

  const getPhotoUrlForDisplay = (photo: any) => {
    return getContractorJobPhotoUrl(photo, job.agencySlug);
  };

  const handlePhotoClick = (photo: any) => {
    setSelectedPhoto(photo);
    setPhotoDialogOpen(true);
  };

  const handleClosePhotoDialog = () => {
    setPhotoDialogOpen(false);
    setSelectedPhoto(null);
  };

  const handleCancelInvite = async (contractorId: string) => {
    try {
      // Find the invite for this contractor to see if we need inviteId path
      const invite = invites.find((i) => String(i.contractorId) === String(contractorId));
      if (invite && !invite.contractorId && invite._id) {
        await cancelInviteByInviteId(job.agencySlug, job._id, invite._id);
      } else if (invite) {
        await cancelContractorInvite(job.agencySlug, job._id, contractorId);
      }
      // Update local state to reflect cancelled (declined) invite
      setInvites((prev) => prev.map((i) =>
        (String(i.contractorId) === String(contractorId)) || (invite && i._id === invite._id)
          ? { ...i, status: 'cancelled' }
          : i
      ));
    } catch (err) {
      console.error('Error cancelling invite:', err);
    }
  };

  const handleResendInvite = async (contractorId: string, invite?: any) => {
    try {
      if (invite && !invite.contractorId && invite._id) {
        await resendInviteByInviteId(job.agencySlug, job._id, invite._id);
      } else {
        await resendContractorInvite(job.agencySlug, job._id, contractorId);
      }
      // Optimistically update invite row without full refresh
      setInvites((prev) => prev.map((i) => {
        if ((invite && i._id === invite._id) || (!invite && String(i.contractorId) === String(contractorId))) {
          return {
            ...i,
            status: 'invited',
            invitedAt: new Date().toISOString(),
            response: undefined,
          };
        }
        return i;
      }));
    } catch (err) {
      console.error('Error resending invite:', err);
    }
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileKey = `photo-${file.name}`;
    
    try {
      setUploadingPhoto(true);
      setUploadProgress(prev => ({ ...prev, [fileKey]: 0 }));

      if (!job.accessToken) {
        throw new Error('Job accessToken is missing. Cannot upload photo.');
      }

      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          const current = prev[fileKey] || 0;
          if (current < 90) {
            return { ...prev, [fileKey]: current + 10 };
          }
          return prev;
        });
      }, 100);

      await uploadJobPhoto(job.accessToken, file);
      
      clearInterval(progressInterval);
      setUploadProgress(prev => ({ ...prev, [fileKey]: 100 }));
      
      // Refresh job data to show new photo
      window.location.reload();
      
    } catch (error) {
      console.error('Error uploading photo:', error);
    } finally {
      setUploadingPhoto(false);
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[fileKey];
        return newProgress;
      });
      // Reset file input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted': return 'success';
      case 'declined': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getJobStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'primary';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check for responses in both new and legacy structures
  const nestedResponses = job.agencyQuote?.responses || [];
  const legacyResponses = job.contractorQuoteResponses || [];
  const hasResponses = nestedResponses.length > 0 || legacyResponses.length > 0;
  const isAssigned = job.status !== 'draft' && job.contractorId;
  const isCancelled = job.status === 'cancelled';
  const acceptedResponse = nestedResponses.find((r: any) => r.status === 'accepted') || 
                          legacyResponses.find((r: ContractorResponse) => r.status === 'accepted');

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        {/* Job Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {contractorTypeName || job.contractorType.charAt(0).toUpperCase() + job.contractorType.slice(1)} Job
            </Typography>
            <Box display="flex" gap={1} alignItems="center" mb={1}>
              <Chip
                label={job.status.replace('_', ' ')}
                color={getJobStatusColor(job.status) as any}
                size="small"
              />
              {job.quoteStatus && job.quoteStatus !== 'none' && (
                <Chip
                  label={`Quote: ${job.quoteStatus}`}
                  color="info"
                  size="small"
                />
              )}
            </Box>
            {job.notes && (
              <Typography variant="body2" color="text.secondary">
                {job.notes}
              </Typography>
            )}
            {(job.startDate || job.finishDate) && (
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                {job.startDate && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Start:</strong> {new Date(job.startDate).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </Typography>
                )}
                {job.finishDate && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Finish:</strong> {new Date(job.finishDate).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title="View job details">
              <IconButton
                size="small"
                onClick={() => onViewJobDetails(job)}
              >
                <ViewIcon />
              </IconButton>
            </Tooltip>
            {isAdmin && !isAssigned && job.status !== 'cancelled' && (
              <>
                {job.quoteStatus !== 'accepted' && (
                  <Tooltip title="Edit job">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onEditJob && onEditJob(job)}
                    >
                      Edit
                    </Button>
                  </Tooltip>
                )}
                <Tooltip title="Send quotes to contractors">
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => onSendQuotes(job)}
                  >
                    Send Quotes
                  </Button>
                </Tooltip>
                <Tooltip title="Cancel job">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onCancelJob(job)}
                  >
                    <CancelIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete job">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDeleteJob(job)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>

        {/* Job Photos */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhotoIcon fontSize="small" />
              Job Photos {job.photos && job.photos.length > 0 && `(${job.photos.length})`}
            </Typography>
            {isAdmin && !isCancelled && (
              <Button
                variant="outlined"
                size="small"
                component="label"
                startIcon={<CloudUploadIcon />}
                disabled={uploadingPhoto}
              >
                {uploadingPhoto ? 'Uploading...' : 'Add Photo'}
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                />
              </Button>
            )}
          </Box>
          
          {/* Upload Progress */}
          {Object.keys(uploadProgress).length > 0 && (
            <Box sx={{ mb: 2 }}>
              {Object.entries(uploadProgress).map(([key, progress]) => (
                <Box key={key} sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Uploading {key.replace('photo-', '')}...
                  </Typography>
                  <LinearProgress variant="determinate" value={progress} sx={{ mt: 0.5 }} />
                </Box>
              ))}
            </Box>
          )}
          
          {job.photos && job.photos.length > 0 ? (
            <Grid container spacing={1}>
              {job.photos.map((photo: any, index: number) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box
                    sx={{
                      position: 'relative',
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        boxShadow: 2,
                      }
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="140"
                      image={getPhotoUrlForDisplay(photo)}
                      alt={photo.filename || `Job photo ${index + 1}`}
                      sx={{
                        objectFit: 'cover',
                        cursor: 'pointer',
                      }}
                      onClick={() => handlePhotoClick(photo)}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                        p: 1,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'white',
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {photo.filename || `Photo ${index + 1}`}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Alert severity="info">
              <Typography variant="body2">
                No photos uploaded yet. {isAdmin && !isCancelled ? 'Click "Add Photo" to upload job photos.' : 'Photos will appear here when uploaded.'}
              </Typography>
            </Alert>
          )}
        </Box>

        {/* Job Status */}
        {isAssigned && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <AssignmentIcon />
              <Typography variant="body2">
                <strong>Assigned to:</strong> {job.contractorName} ({job.contractorEmail})
              </Typography>
            </Box>
          </Alert>
        )}

        {isCancelled && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <CancelIcon />
              <Typography variant="body2">
                <strong>Job Cancelled</strong> - This job has been cancelled and is no longer active.
              </Typography>
            </Box>
          </Alert>
        )}

        {/* Quote Requests Sent to Contractors */}
        {invites && invites.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              Quote Requests Sent ({invites.length})
            </Typography>
            
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Contractor</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Response</TableCell>
                    <TableCell>Sent</TableCell>
                    {isAdmin && !isAssigned && !isCancelled && (
                      <TableCell>Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invites.map((invite: any, index: number) => {
                    // Look for responses in the new nested structure first
                    const nestedResponses = job.agencyQuote?.responses || [];
                    const matchedResponse = nestedResponses.find((r: any) => 
                      String(r.contractorId) === String(invite.contractorId) ||
                      String(r.contractorEmail).toLowerCase() === String(invite.email).toLowerCase()
                    );
                    
                    // Fallback to legacy contractorQuoteResponses
                    const legacyResponse = (job.contractorQuoteResponses || []).find((r: any) => String(r.contractorId) === String(invite.contractorId));
                    
                    // Fallback to single contractorQuote if it matches this invite by contractor or email
                    const matchesSingleQuote = Boolean(
                      job.contractorQuote && (
                        (invite.contractorId && String(job.contractorId) === String(invite.contractorId)) ||
                        (invite.email && job.contractorEmail && String(invite.email).toLowerCase() === String(job.contractorEmail).toLowerCase())
                      )
                    );
                    const singleQuote = matchesSingleQuote && job.contractorQuote
                      ? { amount: job.contractorQuote.amount, notes: job.contractorQuote.notes, respondedAt: job.contractorQuote.sentAt }
                      : null;
                    
                    // Use the first available response (prioritize new structure)
                    const displayResponse = invite.response || 
                      (matchedResponse ? { 
                        amount: matchedResponse.amount, 
                        notes: matchedResponse.notes, 
                        respondedAt: matchedResponse.respondedAt 
                      } : null) ||
                      (legacyResponse ? { 
                        amount: legacyResponse.amount, 
                        notes: legacyResponse.notes, 
                        respondedAt: legacyResponse.respondedAt 
                      } : null) || 
                      singleQuote;
                    
                    // Determine the effective status based on responses
                    let effectiveStatus = invite.status;
                    if (matchedResponse) {
                      effectiveStatus = matchedResponse.status === 'pending' ? 'responded' : matchedResponse.status;
                    } else if (legacyResponse) {
                      effectiveStatus = legacyResponse.status === 'pending' ? 'responded' : legacyResponse.status;
                    } else if (singleQuote) {
                      effectiveStatus = 'responded';
                    }
                    return (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {invite.name || 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {invite.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={effectiveStatus}
                            color={
                            effectiveStatus === 'responded' ? 'success' :
                            effectiveStatus === 'accepted' ? 'success' :
                            effectiveStatus === 'declined' ? 'error' :
                            effectiveStatus === 'cancelled' ? 'default' :
                            effectiveStatus === 'not_selected' ? 'warning' :
                              'default'
                            }
                            size="small"
                          />
                      </TableCell>
                      <TableCell>
                        {displayResponse ? (
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              ${displayResponse.amount?.toLocaleString()}
                            </Typography>
                            {displayResponse.notes && (
                              <Typography variant="caption" color="text.secondary">
                                {displayResponse.notes}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No response yet
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(invite.invitedAt)}
                        </Typography>
                      </TableCell>
                      {isAdmin && !isAssigned && !isCancelled && (
                        <TableCell>
                          <Box display="flex" gap={1}>
                            {effectiveStatus === 'responded' && (
                              <>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  startIcon={<AcceptIcon />}
                                  onClick={() => handleAcceptQuote(invite.contractorId)}
                                  disabled={acceptingQuote === invite.contractorId}
                                >
                                  Approve
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  startIcon={<DeclineIcon />}
                                  onClick={() => handleDeclineQuote(invite.contractorId)}
                                  disabled={decliningQuote === invite.contractorId}
                                >
                                  Decline
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="info"
                                  onClick={() => handleResendInvite(invite.contractorId, invite)}
                                >
                                  Resend
                                </Button>
                              </>
                            )}
                            {effectiveStatus === 'invited' && (
                              <>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  onClick={() => handleResendInvite(invite.contractorId, invite)}
                                >
                                  Resend
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  onClick={() => handleCancelInvite(invite.contractorId)}
                                >
                                  Cancel
                                </Button>
                              </>
                            )}
                            {effectiveStatus === 'declined' && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                  onClick={() => handleResendInvite(invite.contractorId, invite)}
                              >
                                Resend
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  );})}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Contractor Responses */}
        {hasResponses && (
          <Box>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              Contractor Responses ({nestedResponses.length + legacyResponses.length})
            </Typography>
            
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Contractor</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>Responded</TableCell>
                    <TableCell>Status</TableCell>
                    {isAdmin && !isAssigned && !isCancelled && (
                      <TableCell>Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Show responses from new nested structure first */}
                  {nestedResponses.map((response: any, index: number) => (
                    <TableRow key={`nested-${index}`}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {response.contractorName || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {response.contractorEmail}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          ${response.amount?.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {response.notes || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(response.respondedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={response.status}
                          color={
                            response.status === 'accepted' ? 'success' :
                            response.status === 'declined' ? 'error' :
                            'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      {isAdmin && !isAssigned && !isCancelled && (
                        <TableCell>
                          <Box display="flex" gap={1}>
                            {response.status === 'pending' && (
                              <>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  startIcon={<AcceptIcon />}
                                  onClick={() => handleAcceptQuote(response.contractorId)}
                                  disabled={acceptingQuote === response.contractorId}
                                >
                                  Approve
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  startIcon={<DeclineIcon />}
                                  onClick={() => handleDeclineQuote(response.contractorId)}
                                  disabled={decliningQuote === response.contractorId}
                                >
                                  Decline
                                </Button>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  
                  {/* Show responses from legacy structure */}
                  {legacyResponses.map((response: ContractorResponse, index: number) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {response.contractorName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {response.contractorEmail}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <MoneyIcon fontSize="small" color="primary" />
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(response.amount)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {response.notes || 'No notes'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {formatDate(response.respondedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={response.status}
                          color={getStatusColor(response.status) as any}
                          size="small"
                        />
                      </TableCell>
                      {isAdmin && !isAssigned && !isCancelled && (
                        <TableCell>
                          <Box display="flex" gap={0.5}>
                            {response.status === 'pending' && (
                              <>
                                <Tooltip title="Accept quote">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleAcceptQuote(response.contractorId)}
                                    disabled={acceptingQuote === response.contractorId}
                                  >
                                    <AcceptIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Decline quote">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeclineQuote(response.contractorId)}
                                    disabled={decliningQuote === response.contractorId}
                                  >
                                    <DeclineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            {response.status === 'accepted' && (
                              <Chip
                                label="Approved"
                                color="success"
                                size="small"
                                icon={<AcceptIcon />}
                              />
                            )}
                            {response.status === 'declined' && (
                              <Chip
                                label="Declined"
                                color="error"
                                size="small"
                                icon={<DeclineIcon />}
                              />
                            )}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Accepted Quote Summary */}
            {acceptedResponse && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    ✅ Accepted Quote
                  </Typography>
                  <Typography variant="body2">
                    <strong>{acceptedResponse.contractorName}</strong> - {formatCurrency(acceptedResponse.amount)}
                    {acceptedResponse.notes && ` - ${acceptedResponse.notes}`}
                  </Typography>
                </Box>
              </Alert>
            )}

            {/* No Responses Message */}
            {!hasResponses && (
              <Alert severity="info">
                <Typography variant="body2">
                  No contractor responses yet. Send quotes to contractors to get responses.
                </Typography>
              </Alert>
            )}
          </Box>
        )}

        {/* No Responses */}
        {!hasResponses && !isCancelled && (
          <Alert severity="info">
            <Typography variant="body2">
              No contractor responses yet. Send quotes to contractors to get responses.
            </Typography>
          </Alert>
        )}

        {/* Cancelled Job Message */}
        {isCancelled && (
          <Alert severity="warning">
            <Typography variant="body2">
              This job has been cancelled. No further actions can be taken.
            </Typography>
          </Alert>
        )}
      </CardContent>

      {/* Photo Dialog */}
      <Dialog
        open={photoDialogOpen}
        onClose={handleClosePhotoDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ color: 'white' }}>
            {selectedPhoto?.filename || 'Photo'}
          </Typography>
          <IconButton onClick={handleClosePhotoDialog} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {selectedPhoto && (
            <img
              src={getPhotoUrlForDisplay(selectedPhoto)}
              alt={selectedPhoto.filename || 'Photo'}
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
              }}
            />
          )}
        </DialogContent>
        {selectedPhoto && (
          <DialogActions sx={{ justifyContent: 'space-between', padding: 2 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Uploaded: {new Date(selectedPhoto.uploadedAt).toLocaleString()}
            </Typography>
            <Button
              onClick={() => window.open(getPhotoUrlForDisplay(selectedPhoto), '_blank')}
              variant="outlined"
              sx={{ color: 'white', borderColor: 'white' }}
            >
              Open in New Tab
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Card>
  );
};
